import {
  Alert,
  Button,
  Collapse,
  Drawer,
  Empty,
  Input,
  Modal,
  Skeleton,
  Table,
  Tabs,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { computerReleaseNotesApi } from '@/services/computerReleaseNotesApi';
import type {
  ComputerReleaseDraftSummary,
  ReleasePublicationStatus,
} from '@/services/computerReleaseNotesClient';
import { ComputerReleaseDraftStore } from '@/stores/computerReleaseDraftStore';
import { ComputerReleaseEditorStore } from '@/stores/computerReleaseEditorStore';
import './release-notes.css';

const testRecipientUserId = '2109548694486388736';
const statusLabels: Record<ReleasePublicationStatus, string> = {
  unpublished: '未发布',
  published: '已发布',
  unpublishedChanges: '有未发布修改',
  unknown: '状态待接入',
};
const timeFormatter = new Intl.DateTimeFormat('zh-CN', {
  dateStyle: 'medium',
  timeStyle: 'short',
  hour12: false,
});
function time(value?: string) {
  return value ? timeFormatter.format(new Date(value)) : '—';
}

function PublicationStatus({ status }: { status: ReleasePublicationStatus }) {
  return (
    <span className={`release-status release-status-${status}`}>
      {statusLabels[status]}
    </span>
  );
}

export const ComputerReleaseNotesPage = observer(
  function ComputerReleaseNotesPage() {
    const [catalog] = useState(
      () => new ComputerReleaseDraftStore(computerReleaseNotesApi),
    );
    const [editor] = useState(
      () =>
        new ComputerReleaseEditorStore(computerReleaseNotesApi, (draft) =>
          catalog.applyUpdatedDraft(draft),
        ),
    );
    const [filter, setFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [modal, contextHolder] = Modal.useModal();

    useEffect(() => {
      void catalog.refresh();
      return () => {
        catalog.dispose();
        editor.dispose();
      };
    }, [catalog, editor]);
    useEffect(() => {
      if (!editor.dirty && !editor.busy) return;
      const warn = (event: BeforeUnloadEvent) => {
        event.preventDefault();
        event.returnValue = '';
      };
      window.addEventListener('beforeunload', warn);
      return () => window.removeEventListener('beforeunload', warn);
    }, [editor.dirty, editor.busy]);

    const discardThen = (action: () => void) => {
      if (editor.busy) return;
      if (!editor.dirty) {
        action();
        return;
      }
      modal.confirm({
        title: '放弃尚未保存的修改？',
        content: '已保存的草稿会保留，当前编辑区的修改将被放弃。',
        okText: '放弃修改',
        cancelText: '继续编辑',
        onOk: () => {
          if (!editor.busy) action();
        },
      });
    };
    const open = (version: string) =>
      discardThen(() => void editor.open(version));
    const publish = () => {
      if (!editor.canPublish) return;
      modal.confirm({
        title: `发布 Computer ${editor.version} 的更新消息？`,
        content: (
          <div className="space-y-3">
            <p>{editor.title}</p>
            <p>
              将发送给测试账号 {testRecipientUserId}，用户可在 Tutti
              小铃铛中查看。
            </p>
          </div>
        ),
        okText: '确认发布',
        cancelText: '返回编辑',
        onOk: () => {
          void editor.publish();
        },
      });
    };

    const query = search.trim().toLocaleLowerCase();
    const visible = catalog.drafts.filter(
      (row) =>
        (filter === 'all' || row.publicationStatus === filter) &&
        (!query ||
          `${row.version} ${row.editorial?.title ?? row.headline}`
            .toLocaleLowerCase()
            .includes(query)),
    );
    const columns: ColumnsType<ComputerReleaseDraftSummary> = [
      {
        title: '版本 / 更新标题',
        key: 'version',
        width: '40%',
        render: (_, row) => (
          <div className="min-w-0">
            <span className="release-version">Computer {row.version}</span>
            <p className="mt-1 break-words text-sm text-muted-strong">
              {row.editorial?.title ?? row.headline}
            </p>
          </div>
        ),
      },
      {
        title: '公告状态',
        key: 'status',
        width: 160,
        render: (_, row) => (
          <PublicationStatus status={row.publicationStatus} />
        ),
      },
      {
        title: '最近编辑 / 公告发布时间',
        key: 'updatedAt',
        align: 'right',
        width: 240,
        render: (_, row) => (
          <div className="release-date">
            <div>{time(row.editorial?.updatedAt)}</div>
            <p className="mt-1 text-xs text-muted-strong">
              发布：{time(row.editorial?.publishedAt)}
            </p>
          </div>
        ),
      },
      {
        title: '操作',
        key: 'action',
        align: 'right',
        width: 130,
        render: (_, row) => (
          <Button disabled={editor.busy} onClick={() => open(row.version)}>
            查看 / 编辑
          </Button>
        ),
      },
    ];
    const detail = editor.detail;

    return (
      <div className="release-notes space-y-6">
        {contextHolder}
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Computer 发布信息</h1>
            <p className="mt-2 text-sm text-muted-strong">
              编辑版本更新公告，保存草稿后再发布给用户。
            </p>
          </div>
          <Button
            onClick={() => void catalog.refresh()}
            loading={catalog.loading}
            disabled={editor.busy}
          >
            刷新版本
          </Button>
        </header>
        <section aria-label="版本发布信息" className="release-list">
          <Tabs
            activeKey={filter}
            onChange={setFilter}
            items={[
              { key: 'all', label: '全部版本' },
              ...(
                ['unpublished', 'published', 'unpublishedChanges'] as const
              ).map((key) => ({ key, label: statusLabels[key] })),
            ]}
          />
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <Input.Search
              className="release-search"
              aria-label="搜索已加载的版本"
              placeholder="搜索已加载的版本或标题"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              allowClear
            />
            <p className="text-sm text-muted-strong">
              已加载 {catalog.drafts.length} 个版本 · 当前显示 {visible.length}{' '}
              个
            </p>
          </div>
          {catalog.listError ? (
            <Alert
              className="mb-5"
              type="error"
              showIcon
              title="读取发布信息失败"
              description={catalog.listError}
              action={
                <Button onClick={() => void catalog.refresh()}>重试</Button>
              }
            />
          ) : null}
          <div className="release-desktop">
            <Table
              className="release-notes-table"
              rowKey="version"
              columns={columns}
              dataSource={visible}
              loading={catalog.loading}
              pagination={false}
              size="middle"
              scroll={{ x: 760 }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      search || filter !== 'all'
                        ? '已加载的版本中没有符合条件的发布信息。'
                        : '还没有收到 Computer 发布信息。'
                    }
                  />
                ),
              }}
            />
          </div>
          <div className="release-mobile">
            {catalog.loading && !visible.length ? (
              <Skeleton active={false} paragraph={{ rows: 4 }} />
            ) : null}
            {!catalog.loading && !visible.length ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="没有符合条件的发布信息。"
              />
            ) : null}
            {visible.map((row) => (
              <article className="release-mobile-row" key={row.version}>
                <div className="flex items-center justify-between gap-3">
                  <span className="release-version">
                    Computer {row.version}
                  </span>
                  <PublicationStatus status={row.publicationStatus} />
                </div>
                <p className="mt-2 break-words text-sm">
                  {row.editorial?.title ?? row.headline}
                </p>
                <div className="mt-3 flex items-center justify-between gap-3">
                  <p className="text-xs text-muted-strong">
                    公告发布：{time(row.editorial?.publishedAt)}
                  </p>
                  <Button
                    disabled={editor.busy}
                    onClick={() => open(row.version)}
                  >
                    查看 / 编辑
                  </Button>
                </div>
              </article>
            ))}
          </div>
          <footer className="mt-5 flex flex-wrap items-center justify-between gap-3 text-sm text-muted-strong">
            <p>公告状态记录消息是否已发送。接收范围：指定测试账号。</p>
            {catalog.nextPageToken ? (
              <Button
                loading={catalog.loading}
                onClick={() => void catalog.loadMore()}
              >
                加载更多版本
              </Button>
            ) : null}
          </footer>
        </section>
        <Drawer
          title={`编辑发布信息 · Computer ${editor.version}`}
          open={Boolean(editor.version)}
          onClose={() => discardThen(() => editor.close())}
          width="min(100vw, 1040px)"
          maskClosable={!editor.busy}
          keyboard={!editor.busy}
          closable={!editor.busy}
          className="release-editor"
          footer={
            detail ? (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span className="text-sm text-muted-strong">
                  {editor.busy
                    ? '正在保存操作结果，请稍候…'
                    : editor.dirty
                      ? '有尚未保存的修改'
                      : detail.editorial
                        ? `已保存 · 修订 ${editor.revision}`
                        : '尚未保存编辑稿'}
                </span>
                <div className="flex gap-2">
                  <Button
                    disabled={!editor.canSave}
                    loading={editor.saving}
                    onClick={() => void editor.save()}
                  >
                    保存草稿
                  </Button>
                  <Button
                    className="release-publish"
                    type="primary"
                    disabled={!editor.canPublish}
                    loading={editor.publishing}
                    onClick={publish}
                  >
                    发布更新消息
                  </Button>
                </div>
              </div>
            ) : null
          }
        >
          {editor.loading ? (
            <Skeleton active={false} paragraph={{ rows: 10 }} />
          ) : null}
          {editor.error ? (
            <Alert
              className="mb-5"
              type="error"
              showIcon
              title="操作未完成"
              description={editor.error}
              action={
                <Button
                  disabled={editor.busy}
                  onClick={() =>
                    discardThen(() => void editor.open(editor.version))
                  }
                >
                  重新读取版本
                </Button>
              }
            />
          ) : null}
          {editor.notice ? (
            <Alert
              className="mb-5"
              type="success"
              showIcon
              title={editor.notice}
            />
          ) : null}
          {detail ? (
            <>
              <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                <PublicationStatus status={detail.publicationStatus} />
                <a
                  href={detail.runUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-sm underline underline-offset-4"
                >
                  GitHub 发布任务
                </a>
              </div>
              {detail.publicationStatus === 'unknown' ? (
                <Alert
                  className="mb-6"
                  type="info"
                  showIcon
                  title="草稿保存与发布状态尚未接入"
                  description="当前可以查看原始发布信息。服务端更新后，可保存编辑稿并查看准确的发布状态。"
                />
              ) : null}
              {detail.publicationStatus === 'unpublishedChanges' ? (
                <p className="mb-6 text-sm text-muted-strong">
                  此前的公告已发布；当前修订包含尚未发布的修改。
                </p>
              ) : null}
              <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
                <section
                  aria-label="编辑更新公告"
                  className="min-w-0 space-y-5"
                >
                  <div>
                    <label
                      className="mb-2 block text-sm font-medium"
                      htmlFor="release-title"
                    >
                      公告标题
                    </label>
                    <Input
                      id="release-title"
                      value={editor.title}
                      onChange={(event) => editor.setTitle(event.target.value)}
                      disabled={editor.busy}
                      showCount
                      maxLength={120}
                    />
                  </div>
                  <div>
                    <label
                      className="mb-2 block text-sm font-medium"
                      htmlFor="release-content"
                    >
                      更新内容
                    </label>
                    <Input.TextArea
                      id="release-content"
                      value={editor.content}
                      onChange={(event) =>
                        editor.setContent(event.target.value)
                      }
                      disabled={editor.busy}
                      autoSize={{ minRows: 12, maxRows: 24 }}
                      showCount
                      maxLength={8000}
                    />
                  </div>
                  {editor.validationError ? (
                    <output className="block text-sm text-muted-strong">
                      {editor.validationError}
                    </output>
                  ) : null}
                  <p className="text-sm text-muted-strong">
                    {editor.dirty || !detail.editorial
                      ? '先保存草稿，再确认发布。'
                      : detail.publicationStatus === 'published'
                        ? '当前修订已发布。编辑并保存后，可发布新的修订。'
                        : '草稿已保存，可以发布更新消息。'}
                  </p>
                  <Collapse
                    size="small"
                    items={[
                      {
                        key: 'original',
                        label: '查看原始发布摘要',
                        children: (
                          <div className="space-y-3">
                            <p className="font-medium">{detail.headline}</p>
                            <pre className="max-h-64 overflow-y-auto whitespace-pre-wrap break-words font-sans text-sm leading-6">
                              {detail.bodyMarkdown}
                            </pre>
                          </div>
                        ),
                      },
                    ]}
                  />
                </section>
                <aside
                  aria-label="用户通知预览"
                  className="release-preview min-w-0"
                >
                  <h2 className="text-sm font-medium text-muted-strong">
                    用户通知预览
                  </h2>
                  <p className="mt-5 text-xs text-muted-strong">
                    Tutti 更新 · Computer {detail.version}
                  </p>
                  <h3 className="mt-3 break-words text-base font-semibold">
                    {editor.title || '公告标题'}
                  </h3>
                  <p className="mt-3 max-h-96 overflow-y-auto whitespace-pre-wrap break-words text-sm leading-6 [overflow-wrap:anywhere]">
                    {editor.content || '填写更新内容后，可在这里预览。'}
                  </p>
                  <dl className="mt-6 space-y-3 border-t pt-5 text-xs text-muted-strong">
                    <div>
                      <dt>Computer 产物发布时间</dt>
                      <dd className="mt-1">
                        {time(detail.releasePublishedAt)}
                      </dd>
                    </div>
                    <div>
                      <dt>公告发布时间</dt>
                      <dd className="mt-1">
                        {time(detail.editorial?.publishedAt)}
                      </dd>
                    </div>
                    <div>
                      <dt>源码提交</dt>
                      <dd className="release-version mt-1">
                        {detail.sourceSha.slice(0, 12)}
                      </dd>
                    </div>
                  </dl>
                </aside>
              </div>
            </>
          ) : null}
        </Drawer>
      </div>
    );
  },
);
